<template>
	<div id="shopping-cart">
		<!-- A guest has no account, hence nothing active or in progress: only the cart
		     column is rendered, laid out by subscription-columns--cart-only. -->
		<div class="subscription-columns" :class="{ 'subscription-columns--cart-only': is_guest }">
			<subscription-column
				v-if="!is_guest"
				:subscription="active"
				carttype="active"
				:heading="__('Your active subscription')"
				:users="number_of_users"
				@get_subscription="get_subscription()"
			></subscription-column>
			<subscription-column
				v-if="!is_guest"
				:subscription="in_progress"
				carttype="in-progress"
				:heading="__('Your orders in progress')"
				:users="number_of_users"
				@get_subscription="get_subscription()"
			></subscription-column>
			<subscription-column
				:subscription="cart"
				carttype="shopping-cart"
				:heading="__('Your cart')"
				:users="number_of_users"
				@get_subscription="get_subscription()"
			></subscription-column>
		</div>
	</div>
</template>


<script setup>
import SubscriptionColumn from './SubscriptionColumn.vue';

import { ref, computed, onMounted} from 'vue'

// Read once: a visitor stays a guest or not for the life of the page - logging in goes
// through /login and loads /subscription again.
const is_guest = ioi_partners.is_guest()

const subscription_items = ref([])
const active = ref({})
const cart = ref({})
const in_progress = ref({})

// Computed once over the full, unfiltered subscription_items array - not per bucket,
// otherwise a product's users would be undercounted whenever they're split across
// buckets (e.g. some seats active, some still in the basket). Passed down to every
// <subscription-column>.
const number_of_users = computed(() => {
	return subscription_items.value.reduce((count, item) => {
		if (item.unit_of_measure == "User") {
			count += item.qty_in_basket + item.qty_ordered + item.active_qty
		}
		return count
	}, 0)
})

function get_subscription() {
	// A guest has no server cart (the call's 403 would raise frappe's blocking modal):
	// the cart column is built from the local guest cart instead.
	if (is_guest) {
		load_guest_cart()
		return
	}
	// No argument: the server resolves the cart from the session alone.
	frappe.call({
		method: "ioi_partners.www.subscription.get_subscription"
	}).then(r => {
		if (Object.keys(r.message).length) {
			subscription_items.value = r.message.subscription_items
			// Locked (and Archive) items match none of these three filters and are
			// therefore hidden from every column, by design - not by an extra guard.
			active.value = Object.assign({}, r.message, {subscription_items: subscription_items.value.filter(f => f.status == "Active")});
			in_progress.value = Object.assign({}, r.message, {subscription_items: subscription_items.value.filter(f => ["Inactive", "Under Process"].includes(f.status))});
			cart.value = Object.assign({}, r.message, {subscription_items: subscription_items.value.filter(f => f.qty_in_basket > 0)});
		}
	})
}

// Guest counterpart of get_subscription(): quantities from the local guest cart, prices
// from the catalogue at render time (guest_cart.to_subscription). A newer change can land
// while a lookup is in flight, so only the latest answer is applied.
let guest_request = 0

function load_guest_cart() {
	const request = ++guest_request
	ioi_partners.guest_cart.to_subscription()
		.then(subscription => {
			if (request === guest_request) {
				apply_guest_cart(subscription)
			}
		})
		.catch(error => {
			// Not expected - to_subscription() handles its own lookup failures - so it is
			// logged, and shown like a failed lookup rather than leaving the column frozen.
			console.error("Guest cart could not be built", error)
			if (request === guest_request) {
				apply_guest_cart(null)
			}
		})
}

// The priced lines become ordinary cards. The guest key carries what SubscriptionColumn
// renders on its own: the stored lines a failed lookup could not price - a failed lookup
// never looks like an empty cart, same rule as the floating cart - and the lines the
// catalogue no longer returns, which the visitor can remove there.
function apply_guest_cart(subscription) {
	const stored = ioi_partners.guest_cart.items()
	const lines = subscription ? subscription.subscription_items : []
	subscription_items.value = lines
	cart.value = Object.assign({}, subscription || {}, {
		subscription_items: lines,
		guest: {
			failed: (!subscription || subscription.failed) && stored.length > 0,
			unpriced: subscription ? subscription.unpriced : stored,
			unavailable: subscription ? subscription.unavailable : [],
		},
	})
}

onMounted(() => {
	get_subscription()
	// A guest's cart changes from this page's steppers or from another tab; every change
	// rebuilds the column from the local guest cart.
	if (is_guest) {
		window.addEventListener(ioi_partners.guest_cart.CHANGE_EVENT, () => get_subscription())
	} else {
		// A guest cart merged after login (guest_cart_merge.js) lands after this page has
		// already loaded the account's cart.
		window.addEventListener(ioi_partners.guest_cart_merge.MERGED_EVENT, () => get_subscription())
	}
})

</script>

<style scoped lang="scss">
#shopping-cart {
	margin: 1.5rem 0;
}
</style>